import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ShoppingBag } from 'lucide-react';
import { api, post } from '../../lib/api';
import { formatBRL } from '../../lib/format';
import { Alert, Button, Spinner } from '../../ui/primitives';
import { useToast } from '../../ui/feedback';
import { useCart } from '../../menu/cart';
import { MenuBrowser } from '../../menu/MenuBrowser';
import { ProductSheet } from '../../menu/ProductSheet';
import { CartSheet } from '../../menu/CartSheet';
import type { MenuCategory, MenuProduct, PublicMenu } from '../../menu/types';
import { useTables } from './StaffTables';
import { staffPath } from '../../lib/paths';

/** Garçom lança pedido na comanda da mesa. Usa o mesmo cardápio e as mesmas regras do cliente. */
export function StaffOrderEntry() {
  const { tableId = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const tables = useTables();
  const menu = useQuery({ queryKey: ['public-menu'], queryFn: () => api<PublicMenu>('/api/public/menu') });
  const table = tables.data?.find((t) => t.id === tableId);
  const cart = useCart(`garlic:staff-cart:${tableId}`);
  const [selected, setSelected] = useState<{ p: MenuProduct; c: MenuCategory } | null>(null);
  const [cartOpen, setCartOpen] = useState(false);

  if (tables.isPending || menu.isPending) return <Spinner />;
  if (!table) return <Alert tone="danger">Mesa não encontrada.</Alert>;
  if (!table.session) return <Alert tone="warning">A mesa {table.label} não tem comanda aberta. Abra a comanda antes de lançar pedidos.</Alert>;
  if (menu.isError) return <Alert tone="danger">Não foi possível carregar o cardápio.</Alert>;
  const sessionId = table.session.id;

  return (
    <div className="pb-24">
      <Link to={staffPath('/mesas')} className="inline-flex items-center gap-1 text-sm text-ink-3 hover:text-ink"><ArrowLeft className="size-4" /> Mesas</Link>
      <h1 className="mt-2 font-display text-2xl font-semibold">Lançar pedido · Mesa {table.label}</h1>
      <p className="text-sm text-ink-3">Comanda {table.session.number}</p>
      <MenuBrowser categories={menu.data.categories} onSelect={(p, c) => setSelected({ p, c })} />
      {cart.count > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 px-4 pb-4 lg:left-60">
          <Button size="lg" className="mx-auto flex w-full max-w-3xl justify-between shadow-xl" onClick={() => setCartOpen(true)}>
            <span className="inline-flex items-center gap-2"><ShoppingBag className="size-5" /> Revisar ({cart.count})</span>
            <span className="tabular-nums">{formatBRL(cart.totalCents)}</span>
          </Button>
        </div>
      )}
      <ProductSheet product={selected?.p ?? null} category={selected?.c ?? null} canOrder onClose={() => setSelected(null)} onAdd={cart.add} />
      <CartSheet
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        cart={cart}
        title={`Pedido da mesa ${table.label}`}
        askName={false}
        submitLabel="Lançar na comanda"
        onSubmit={(payload) => post(`/api/staff/sessions/${sessionId}/orders`, payload)}
        onSuccess={(n) => {
          toast.success(`Pedido #${String(n).padStart(4, '0')} lançado na mesa ${table.label}`);
          void qc.invalidateQueries({ queryKey: ['staff-tables'] });
          void qc.invalidateQueries({ queryKey: ['staff-orders'] });
          navigate(staffPath('/mesas'));
        }}
      />
    </div>
  );
}
